import {
    GridApiPro,
    GridDataSource,
    GridFilterModel,
    GridGetRowsParams,
    GridGetRowsResponse,
    GridPaginationModel,
    GridSortModel,
    gridDataRowIdsSelector,
} from '@mui/x-data-grid-pro'
import { csvToExcel, downloadBlob } from '@utils'
import React, { useMemo, useState } from 'react'

interface Props {
    getRows: ({
        paginationModel,
        sortModel,
        filterModel,
    }: {
        paginationModel?: GridPaginationModel
        sortModel?: GridSortModel
        filterModel?: GridFilterModel
    }) => Promise<GridGetRowsResponse>
}

export type UseDataSource = ReturnType<typeof useDataSource>

/**
 * If the user has filtered out the name field in implies they don't want the id
 * We only want to add back in the ID if it's desired
 */
const csvHasNameColumn = (csv: string) => {
    const rows = csv.split('\n')
    const headers = rows[0].split(',')
    const shouldAddId = headers.indexOf('Name') > -1
    return shouldAddId
}

/**
 * The only way MUI gives us for getting all the filtered data from the datatable is via CSV (as far as we can see as of Dec 2024)
 * So we are splitting the id from the name in the csv since this is desired as it's own column in the spreadsheet
 */
function addIdColumnToCsv(csv: string): string {
    const rows = csv.split('\n')
    const headers = rows[0].split(',')
    headers.unshift('id')
    const newHeaderRow = headers.join(',')
    const dataRows = rows.slice(1).map((row) => {
        const rowArr = row.split(',')
        const nameField = rowArr[0]
        const [name, id] = nameField.split(' | ')
        rowArr[0] = name
        rowArr.unshift(id)
        return rowArr.join(',')
    })
    return [newHeaderRow, ...dataRows].join('\n')
}

/** Without premium MUI we have to build our own excel export */
export async function handleExportExcel(
    apiRef: React.MutableRefObject<GridApiPro>,
    filename: string = 'export'
) {
    const csvData = apiRef.current.getDataAsCsv()
    const csvDataToConvert = csvHasNameColumn(csvData)
        ? addIdColumnToCsv(csvData)
        : csvData
    const excelBlob = await csvToExcel(csvDataToConvert)
    await downloadBlob(excelBlob, `${filename}.xlsx`)
}

/**
 * Hook for DataGridPro dataSource and supporting features.
 */
export default function useDataSource({ getRows }: Props) {
    const [exporting, setExporting] = useState<boolean>(false)
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [],
    })

    async function exportAllRows<
        T extends (params: {
            filename: string
            includeHeaders: boolean
        }) => void,
    >(
        apiRef: React.MutableRefObject<GridApiPro>,
        exporter?: T,
        filename: string = 'export'
    ) {
        apiRef.current.setLoading(true)
        setExporting(true)

        const currentRowIds = gridDataRowIdsSelector(apiRef)

        try {
            const sortModel = apiRef.current.getSortModel()
            const totalRows = apiRef.current.getRowsCount()
            const allRows = await dataSource.getRows({
                filterModel,
                sortModel,
                paginationModel: {
                    page: 0,
                    pageSize: totalRows,
                },
                start: 0,
                end: totalRows - 1,
            })

            apiRef.current.updateRows(allRows.rows)

            if (exporter) {
                exporter({
                    filename,
                    includeHeaders: true,
                })
            } else {
                await handleExportExcel(apiRef, filename)
            }

            const idsToDelete = allRows.rows
                .map((row) => row.id)
                .filter((id) => !currentRowIds.includes(id))

            apiRef.current.updateRows(
                idsToDelete.map((id) => ({ id, _action: 'delete' }))
            )
        } finally {
            apiRef.current.setLoading(false)
            setExporting(false)
        }
    }

    const dataSource = useMemo<GridDataSource>(function getData() {
        return {
            getRows: async ({
                paginationModel,
                sortModel,
                filterModel,
            }: GridGetRowsParams) =>
                getRows({
                    paginationModel,
                    sortModel,
                    filterModel,
                }),
        }
    }, [])

    return {
        /**
         * Data source for a DataGridPro instance.
         *
         * @see {@link https://v8.mui.com/x/react-data-grid/tutorials/server-side-data/#10-set-up-griddatasource | Build a Data Grid with Server-side Data}
         */
        dataSource,

        /**
         * Whether the DataGrid is currently exporting its contents.
         */
        exporting,

        /**
         * Sets the current filter model. Use to ensure `exportAllRows` has the
         * correct filter information.
         * @see exportAllRows
         */
        setFilterModel,

        /**
         * Export the data from a server-side filtered and/or sorted grid as a
         * CSV file.
         *
         * Attach `setFilterModel` to DataGrid to ensure correct filtering on
         * exported data.
         *
         * @template {(params: { filename: string; includeHeaders: boolean }) => void} T
         * @param {T} exporter - Export handler for the grid.
         * @param {React.MutableRefObject<GridApiPro>} apiRef - The API reference to the DataGrid that should be used for
         * filtering and sorting.
         * @param {string} [filename='export'] - Name of the exported file
         *
         * @see setFilterModel
         * @see {@link https://v8.mui.com/x/react-data-grid/export/#server-side-export | Data Grid - Export}
         */
        exportAllRows,
    }
}
